'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["match"];
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { NavMarker } from 'react-rhumb';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import TrackAwardModal from '../../components/Modals/SuccessModals/TrackAwardModal';
import UsageTracker from '../../utils/UsageTracker';
import { fetchTrackByIdIfNeeded } from '../../actions/TrackActions';
import { fetchAllAchievements } from '../../actions/AchievementActions';
import { getTrack, getTracksAreRequesting } from '../../selectors/trackSelectors';
import { useTrackAwardProgress, useAllProgress } from '../../features/userProgress';
import { useExamAttempts } from '../../features/exams';
import { jsx as _jsx } from "react/jsx-runtime";
class TrackAwardModalContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.trackViewModal = () => {
      this.trackUsage('view-modal');
    };
    this.trackUsage = this.trackUsage.bind(this);
  }
  componentDidMount() {
    const {
      fetchAchievements,
      fetchTrack,
      language,
      match: {
        params: {
          trackId
        }
      }
    } = this.props;
    this.maybeRedirectToBasePage();
    fetchTrack(trackId, language);
    fetchAchievements(language);
  }
  componentDidUpdate() {
    this.maybeRedirectToBasePage();
  }
  maybeRedirectToBasePage() {
    const {
      history: {
        replace
      },
      match: {
        params
      },
      tracksNotYetLoaded,
      track
    } = this.props;
    if (!tracksNotYetLoaded && this.isAwardInvalid(track)) {
      // only render the award modal if there is an actual award with the same name
      replace(params[0] || '/');
    }
  }
  isAwardInvalid(track) {
    const {
      award
    } = this.props;
    return !award || !track;
  }
  trackUsage(action, socialName) {
    const {
      track
    } = this.props;
    const trackedProps = {
      action,
      trackId: track.get('id'),
      trackName: track.get('name'),
      view: 'track-award-modal'
    };
    if (typeof socialName === 'string') {
      trackedProps.socialName = socialName;
    }
    UsageTracker.track('awardInteraction', trackedProps);
  }
  render() {
    const {
      award,
      examAttempt,
      firstName,
      history: {
        push
      },
      location: {
        search
      },
      track,
      tracksNotYetLoaded,
      match: {
        params = []
      },
      linkedTrackId,
      linkedTrackLanguage,
      progressLoading
    } = this.props;
    if (!(track && track.get('id')) && tracksNotYetLoaded) {
      return /*#__PURE__*/_jsx(LoadingOverlay, {});
    }
    if (this.isAwardInvalid(track)) {
      return null; // the redirect will do the rest
    }
    const returnUrl = `${params[0] || '/'}${search}`;
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "COURSE_AWARD_VIEW_LOADED",
      children: /*#__PURE__*/_jsx(TrackAwardModal, {
        award: award,
        examAttempt: examAttempt,
        firstName: firstName,
        linkedTrackId: linkedTrackId,
        linkedTrackLanguage: linkedTrackLanguage,
        onClickShareThis: () => this.trackUsage('click-share-this-cert'),
        onClose: (...partialArgs) => push(returnUrl, undefined, ...partialArgs),
        onDownloadCertificate: (...partialArgs) => this.trackUsage('download-certificate', ...partialArgs),
        onMount: this.trackViewModal,
        progressLoading: progressLoading,
        track: track
      })
    });
  }
}
TrackAwardModalContainer.propTypes = {
  award: PropTypes.object,
  examAttempt: PropTypes.object,
  fetchAchievements: PropTypes.func.isRequired,
  fetchTrack: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }).isRequired,
  linkedTrackId: PropTypes.number,
  linkedTrackLanguage: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      trackId: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  progressLoading: PropTypes.bool,
  track: ImmutablePropTypes.map,
  tracksNotYetLoaded: PropTypes.bool
};
function makeMapStateToProps(state, ownProps) {
  const {
    auth: {
      user
    }
  } = state;
  const {
    match: {
      params: {
        trackId
      }
    }
  } = ownProps;
  return {
    track: getTrack(state, {
      trackId
    }),
    tracksNotYetLoaded: getTracksAreRequesting(state),
    firstName: user.firstName
  };
}
function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchTrack: fetchTrackByIdIfNeeded,
    fetchAchievements: fetchAllAchievements
  }, dispatch);
}
const WithAwardAttemptTrackAwardModalContainer = _ref => {
  var _allProgress$assetPro;
  let {
      match
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const trackId = match.params.trackId;
  const {
    award,
    allProgressLoading: trackProgressLoading
  } = useTrackAwardProgress(Number(trackId));
  const {
    allProgress,
    allProgressLoading
  } = useAllProgress({
    language: award === null || award === void 0 ? void 0 : award.language
  });
  const trackAwardId = award === null || award === void 0 ? void 0 : award.trackIdAward;
  const isAwardFromDifferentLanguage = trackAwardId !== trackId;
  const certificationAwardedInDifferentLanguage = trackAwardId && trackAwardId !== Number(trackId);
  const linkedTrackId = certificationAwardedInDifferentLanguage ? trackAwardId : null;
  const linkedLanguage = (_allProgress$assetPro = allProgress.assetProgress.find(asset => asset.type === 'TRACK' && asset.id === trackAwardId)) === null || _allProgress$assetPro === void 0 ? void 0 : _allProgress$assetPro.language;
  const examTrackId = isAwardFromDifferentLanguage ? award === null || award === void 0 ? void 0 : award.trackIdAward : trackId;
  const {
    examAttempts
  } = useExamAttempts(examTrackId);
  const examAttempt = award && examAttempts && award.examAttemptId ? examAttempts[award.examAttemptId] : null;
  if (trackProgressLoading) return null;
  return /*#__PURE__*/_jsx(TrackAwardModalContainer, Object.assign({
    award: award,
    examAttempt: examAttempt,
    language: award === null || award === void 0 ? void 0 : award.language,
    linkedTrackId: linkedTrackId,
    linkedTrackLanguage: linkedLanguage,
    match: match,
    progressLoading: allProgressLoading,
    trackId: trackId
  }, props));
};
export default connect(makeMapStateToProps, mapDispatchToProps)(WithAwardAttemptTrackAwardModalContainer);